import { BLUETICKS_TOKEN_KEY } from "@common/reusable/common";
import { AppThunkDispatch } from "@common/types/common-root-state-type";
import { logout } from "@common-reducers/UserReducer";
import { initAuth } from "./AuthThunks";

export type StorageAuthAction = "login" | "logout" | "ignore";

/**
 * Pure decision core for the chrome.storage.onChanged auth-sync listener.
 *
 * The popup reads the background chrome.storage hub once on mount (via initAuth ->
 * attemptJwtAuth) with no live listener, so when the web-app -> hub bridge completed
 * asynchronously a popup opened in that window stayed stuck on the login form. This
 * function decides how a storage change should move the popup's auth state.
 *
 * Loop-avoidance is critical: the auth flow calls persistToken, which writes bt-token to
 * the hub -> that fires onChanged again with newValue === the token we just authenticated
 * with. We compare against the CURRENT auth token and ignore the echo.
 *
 * @param changes  the `changes` object chrome.storage.onChanged hands the listener
 * @param areaName the storage area ('local' | 'sync' | 'session' | 'managed')
 * @param currentToken the token the popup is currently authenticated with (Redux accessToken)
 */
export function decideStorageAuthAction(
  changes: { [key: string]: chrome.storage.StorageChange } | Record<string, { newValue?: any; oldValue?: any }>,
  areaName: string,
  currentToken: string | null | undefined,
): StorageAuthAction {
  // The hub lives in chrome.storage.local; ignore sync/session/managed.
  if (areaName !== "local") return "ignore";

  const change = changes?.[BLUETICKS_TOKEN_KEY];
  if (!change) return "ignore";

  const newValue = change.newValue ?? undefined;
  const oldValue = change.oldValue ?? undefined;

  // No real change.
  if (newValue === oldValue) return "ignore";

  // Token removed from the hub (logout elsewhere). Only act if we were logged in;
  // logging out while already logged out is pointless noise.
  if (newValue == null) {
    return currentToken ? "logout" : "ignore";
  }

  // Token present. Loop guard: ignore the echo of persistToken's own write (the value
  // we already authenticated with). Only react when the hub token is genuinely different.
  if (newValue === currentToken) return "ignore";

  return "login";
}

/**
 * Subscribe the popup's auth state to the background chrome.storage hub so it live-syncs:
 * log in when bt-token appears/changes, log out when it's removed. Loop-guarded against
 * persistToken's own writes via decideStorageAuthAction.
 *
 * @param dispatch        the (extension) store dispatch
 * @param getCurrentToken returns the token the popup is currently authenticated with
 * @returns an unsubscribe function (remove the listener), or a no-op if storage.onChanged
 *          is unavailable in this context.
 */
export function subscribeStorageAuthSync(
  dispatch: AppThunkDispatch,
  getCurrentToken: () => string | null | undefined,
): () => void {
  const onChanged = typeof chrome !== "undefined" ? chrome.storage?.onChanged : undefined;
  if (!onChanged?.addListener) {
    console.log('[login-flow] storage-auth-sync: storage.onChanged UNAVAILABLE — no live hub sync in this context');
    return () => {
      /* no-op: storage.onChanged not available in this context */
    };
  }
  console.log('[login-flow] storage-auth-sync: listener registered (live hub sync active)');

  // Track the last token we acted on so a burst of echoes (e.g. localStorage + hub writes
  // landing close together) can't double-trigger between the dispatch and the Redux update.
  let lastHandledToken: string | null | undefined = getCurrentToken();

  const handler = (
    changes: { [key: string]: chrome.storage.StorageChange },
    areaName: string,
  ) => {
    // "current token" is normally the Redux accessToken (getCurrentToken). During a
    // brief window mid-initAuth it can read undefined while Redux settles; fall back to
    // the last token we acted on so an echo of our own write isn't misread as a new
    // login. This dual-source is intentional and safe: the logout branch resets
    // lastHandledToken to undefined, so a later re-login with the SAME token is not
    // suppressed (the dedupe is cleared on logout).
    const current = getCurrentToken() ?? lastHandledToken;
    const action = decideStorageAuthAction(changes, areaName, current);
    const ch = changes?.[BLUETICKS_TOKEN_KEY];
    console.log("[login-flow] storage-auth-sync: onChanged", {
      area: areaName,
      btTokenChanged: !!ch,
      newValPresent: ch?.newValue != null,
      oldValPresent: ch?.oldValue != null,
      currentTokenPresent: current != null,
      sameAsCurrent: ch?.newValue != null && ch?.newValue === current,
      action,
    });
    if (action === "ignore") return;

    const newValue = changes?.[BLUETICKS_TOKEN_KEY]?.newValue ?? undefined;

    if (action === "login") {
      // Extra echo guard: if we already acted on this exact token, skip.
      if (newValue === lastHandledToken) return;
      lastHandledToken = newValue;
      // Re-run initAuth rather than authenticating from `newValue` directly: the hub is
      // the source of truth and has just become `newValue`, so attemptJwtAuth reads the
      // freshest token, and it's idempotent (guarded by lastAttemptedJwtToken +
      // isInitAuthRunning). Authenticating from `newValue` via externalAccessToken would
      // re-persist the token and re-fire onChanged (a loop); the one extra get-token IPC
      // round-trip here is a deliberate trade for correctness and reuse of one auth path.
      console.log("[login-flow] storage-auth-sync: hub token changed -> re-running initAuth");
      dispatch(initAuth());
    } else if (action === "logout") {
      lastHandledToken = undefined;
      console.log("[login-flow] storage-auth-sync: hub token removed -> logging out popup");
      dispatch(logout());
    }
  };

  onChanged.addListener(handler);
  return () => {
    try {
      onChanged.removeListener(handler);
    } catch {
      /* ignore */
    }
  };
}
